import React from 'react';
import {
  SnackbarProvider,
  useSnackbar,
  VariantType,
  ProviderContext,
} from 'notistack';
import {RestError} from '@resurs-internal/rb-app-api';
import {styled} from '@mui/material';

let snackbarRef: ProviderContext;
export const SnackbarProviderRef: React.FC = () => {
  snackbarRef = useSnackbar();
  return <></>;
};

const StyledSnackbarProvider = styled(SnackbarProvider)`
  &.SnackbarItem-variantWarning {
    color: black;
  }
`;

export const AppSnackbarProvider: React.FC<
  React.PropsWithChildren<unknown>
> = ({children}) => {
  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <StyledSnackbarProvider maxSnack={3}>
      <SnackbarProviderRef />
      {children}
    </StyledSnackbarProvider>
  );
};

export const showMessage = (
  message: string,
  variant: VariantType = 'default',
) => {
  snackbarRef.enqueueSnackbar(message, {
    variant: variant,
  });
};

export interface TryMutateOptions {
  callback: () => Promise<unknown>;
  success?: string;
  onRestError?: (error: RestError) => void;
}

const tryMutate = async (options: TryMutateOptions) => {
  const onRestError = options.onRestError ?? snackbar.catch;
  try {
    await options.callback();
    if (options.success) snackbar.success(options.success);
  } catch (error) {
    if (isRestError(error)) return onRestError(error);
    snackbar.catch(error);
  }
};

const formatRestError = (error: RestError) => {
  if (!error?.responseBody) return `${error.status}: ${error.message}`;
  return `${error.status}: ${error.responseBody.message}\n${error.responseBody.traceId}`;
};

export const showRestError = (error: RestError) => {
  if (error.status === 401) return; // ignore
  if (0 <= error.status && error.status <= 199) {
    return showMessage(error.message, 'default');
  }
  if (200 <= error.status && error.status <= 299) {
    return showMessage(error.message, 'success');
  }
  const variant = error.status < 500 ? 'warning' : 'error';
  const message = formatRestError(error);
  return showMessage(message, variant);
};

export const snackbar = {
  success: (e: string) => showMessage(e, 'success'),
  info: (e: string) => showMessage(e, 'info'),
  warning: (e: string) => showMessage(e, 'warning'),
  error: (e: string) => showMessage(e, 'error'),
  tryMutate,
  catch: (error: unknown) => {
    if (isRestError(error)) return showRestError(error);
    return snackbar.error(`${error}`);
  },
};

export const isRestError = (e: unknown): e is RestError => {
  if (!e) return false;
  if (typeof e !== 'object') return false;
  return Boolean((e as RestError)?.status);
};
